import React, { Children, ReactNode, useContext, useState } from 'react';
import { Dictionary, globalEllipsisTextStyles, ThemeContext } from 'lib';
import DashboardChart from './DashboardChart';
import {
    Box,
    Card,
    CardContent,
    InputAdornment,
    Typography,
} from '@mui/material';
import {
    ChainMetrics,
    dashboardContainerStyles,
    DEFAULT_METRIC,
    ManageChainsData,
    METRICS,
} from './dashboard-chart-helpers';
import { Autocomplete, Checkbox, TextField } from '@biq/apex-ui';
import SearchIcon from '@mui/icons-material/Search';
import { TruncateIcon } from '../../../../assets/images';
import { TruncateChip } from '../../../../components';

interface DashboardContainerProps {
    data: ChainMetrics;
}

export const DashboardContainer = ({
    data,
}: DashboardContainerProps): React.JSX.Element => {
    const [selectedMetric, setSelectedMetric] = useState<string>('assets');
    const [selectedChains, setSelectedChains] = useState<string[]>([]);
    const [filteredChains, setFilteredChains] = useState<Dictionary[]>([]);
    const [isManageChainsFilterVisible, setIsManageChainsFilterVisible] =
        useState<boolean>(false);
    const { currentTheme } = useContext(ThemeContext);

    const selectMetric = (metric: ManageChainsData): void => {
        if (metric.value !== selectedMetric) {
            setSelectedMetric(metric.value);
            setSelectedChains([]);
        }
    };

    const onHandleChain = (chains: any): void => {
        setSelectedChains(chains);
    };

    const getTruncateIcon = (): ReactNode => {
        return (
            <img
                src={TruncateIcon}
                style={dashboardContainerStyles.truncateIconStyles}
                alt='TRUNCATE_ICON'
            />
        );
    };

    const getTruncateLabel = (value: string): string => {
        return value.length > 12 ? value.slice(0, 12) + '...' : value;
    };

    const onDeleteChain = (value: string): void => {
        setSelectedChains((prev) => {
            return prev.filter((chip: string) => {
                return chip !== value;
            });
        });
    };

    return (
        <Card
            elevation={0}
            sx={{
                ...dashboardContainerStyles.card,
                borderColor: currentTheme.palette.grayScale[200],
            }}
        >
            <CardContent>
                <Box>
                    <Typography
                        variant='overline'
                        sx={
                            dashboardContainerStyles.dashboardChainMetricTextStyles
                        }
                    >
                        CHAIN METRICS
                    </Typography>

                    <Box sx={dashboardContainerStyles.filterContainer}>
                        <Box>
                            <Typography
                                variant='overline'
                                sx={dashboardContainerStyles.filterText}
                            >
                                Filter By
                            </Typography>
                            <Autocomplete
                                options={METRICS}
                                defaultValue={DEFAULT_METRIC}
                                getOptionLabel={(option): string =>
                                    option?.name
                                }
                                renderInput={(params): React.JSX.Element => (
                                    <TextField
                                        {...params}
                                        variant={'outlined'}
                                        sx={
                                            dashboardContainerStyles.autocompleteFilterByTextProps
                                        }
                                        inputProps={{
                                            ...params.inputProps,
                                            readOnly: true,
                                        }}
                                    />
                                )}
                                onChange={(_event, newValue): void =>
                                    selectMetric(newValue)
                                }
                                sx={dashboardContainerStyles.autocomplete}
                                disableClearable
                            />
                        </Box>
                        {isManageChainsFilterVisible && (
                            <Box>
                                <Typography
                                    variant='overline'
                                    sx={dashboardContainerStyles.filterText}
                                >
                                    Manage Chains
                                </Typography>
                                <Autocomplete
                                    options={
                                        filteredChains.map(
                                            (chain) => chain.name
                                        ) || []
                                    }
                                    noOptionsText={
                                        'No results matching that search criteria.'
                                    }
                                    renderTags={(
                                        value: string[]
                                    ): ReactNode => {
                                        return Children.toArray(
                                            value.map(
                                                (
                                                    item: string,
                                                    index: number
                                                ) => {
                                                    return (
                                                        <TruncateChip
                                                            color='primary'
                                                            label={getTruncateLabel(
                                                                item
                                                            )}
                                                            chipStyles={{
                                                                marginLeft:
                                                                    index === 0
                                                                        ? '13px'
                                                                        : '4px',
                                                                marginRight:
                                                                    index === 1
                                                                        ? '6px'
                                                                        : '4px',
                                                                height: '24px !important',
                                                                marginTop:
                                                                    '2px',
                                                                marginBottom:
                                                                    '2px',
                                                            }}
                                                            readOnly={false}
                                                            variant='outlined'
                                                            onDelete={(): void =>
                                                                onDeleteChain(
                                                                    item
                                                                )
                                                            }
                                                        />
                                                    );
                                                }
                                            )
                                        );
                                    }}
                                    disableCloseOnSelect
                                    value={selectedChains}
                                    multiple={true}
                                    limitTags={2}
                                    getLimitTagsText={getTruncateIcon}
                                    renderOption={(
                                        props,
                                        option,
                                        { selected }
                                    ): React.JSX.Element => (
                                        <li
                                            {...props}
                                            style={
                                                dashboardContainerStyles.renderOptionStyles
                                            }
                                        >
                                            <Checkbox
                                                checked={selected}
                                                sx={
                                                    dashboardContainerStyles.renderOptionStyles
                                                }
                                            />
                                            <Typography
                                                sx={globalEllipsisTextStyles}
                                            >
                                                {option}
                                            </Typography>
                                        </li>
                                    )}
                                    renderInput={(
                                        params
                                    ): React.JSX.Element => (
                                        <TextField
                                            {...params}
                                            variant={'outlined'}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: [
                                                    <InputAdornment
                                                        key={'searchIcon'}
                                                        position='start'
                                                    >
                                                        <SearchIcon />
                                                    </InputAdornment>,
                                                    params.InputProps
                                                        .startAdornment ??
                                                        params.InputProps
                                                            .startAdornment,
                                                ],
                                            }}
                                        />
                                    )}
                                    onChange={(_event, newValue): void =>
                                        onHandleChain(newValue)
                                    }
                                    sx={{
                                        ...dashboardContainerStyles.autocompleteManageChainsProps,
                                        ...dashboardContainerStyles.autocomplete,
                                    }}
                                    ListboxProps={{
                                        sx: dashboardContainerStyles.autocompleteManageChainsListboxStyles,
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                <DashboardChart
                    data={data}
                    selectedMetric={selectedMetric}
                    selectedChains={selectedChains}
                    setIsManageChainsFilterVisible={
                        setIsManageChainsFilterVisible
                    }
                    setFilteredChains={setFilteredChains}
                />
            </CardContent>
        </Card>
    );
};
